import React, { useContext } from "react";
import { Box, Card, CardContent, Grid, LinearProgress, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { useSampleEntries } from "../../../../hooks/useReduxSelectors";
import { useSelector } from "react-redux";
import SystemIdContext from "../../../../SystemIdContext";
import { surface } from "../../../../../theme/muiTheme";

import SampleAnalyzerDataCards from "./SampleAnalyzerDataCards";

// MUI's indeterminate LinearProgress animates the bars horizontally via
// `left`/`right`. These mirror that motion onto `top`/`bottom` so the
// animation runs top-to-bottom instead of left-to-right.
const verticalIndeterminate1 = keyframes`
  0% {
    top: -35%;
    bottom: 100%;
  }
  60% {
    top: 100%;
    bottom: -90%;
  }
  100% {
    top: 100%;
    bottom: -90%;
  }
`;

const verticalIndeterminate2 = keyframes`
  0% {
    top: -200%;
    bottom: 100%;
  }
  60% {
    top: 107%;
    bottom: -8%;
  }
  100% {
    top: 107%;
    bottom: -8%;
  }
`;

function SampleItem({ sample }) {

  // console.log("sample", sample);
  const samplesEntries = useSampleEntries();
  const systemId = useContext(SystemIdContext);

  const sampleName =
    samplesEntries &&
    samplesEntries.length > 0 &&
    samplesEntries.find((dev) => sample && dev.hwId === sample.hwId).name;

  const calculateBootstrapClass = (sample) => {
    if (sample.sampleBgColor === "red" && sample.isActive === true) {
      return "danger";
    } else if (sample.sampleBgColor === "red" && sample.isActive === true) {
      return "danger";
    } else if (sample.sampleBgColor === "orange" && sample.isActive === true) {
      return "warning";
    } else {
      return "secondary";
    }
  };

  const colorClass = calculateBootstrapClass(sample.sampleBgColor);

  const sampleAlarms = useSelector(
    ({ realtime }) =>
      realtime[systemId] &&
      realtime[systemId].alarms.entries &&
      realtime[systemId].alarms.entries[sample.hwId] || []
  );

  const getBorderColor = () => {
    if (sample.sampleBgColor === "red" && sample.isActive === true) return "error.main";
    if (sample.sampleBgColor === "orange" && sample.isActive === true) return "warning.main";
    return "divider";
  };

  const getProgressColor = () => {
    if (sample.sampleBgColor === "red" && sample.isActive === true) return "error";
    if (sample.sampleBgColor === "orange" && sample.isActive === true) return "warning";
    return "info";
  };

  return (
    <Card
      sx={{ mt: 0.5, mb: 1.5, color: 'white', bgcolor: 'background.dark', border: 1, borderColor: getBorderColor(), minWidth: 400, maxWidth: 500, width: '100%' }}
      variant="outlined"
    >
      <CardContent>
        <Box sx={{ fontWeight: 600, fontSize: "1.1rem", letterSpacing: "1px" }}>
          {sampleName}
        </Box>
        <Grid container>
          <Grid size={10}>
            <Box sx={{ my: 1, fontSize: "1.1rem", borderBottom: `1px solid ${surface.subtleDivider}`, pb: 1 }}>
              {sample.measurements.map((measurement, index) => {
                let fixedValue = measurement.unit !== undefined ? parseFloat(measurement.value).toFixed(2) : "-";
                return (
                  <Grid container key={measurement.displayName + index}>
                    <Grid size={8}>{measurement.displayName}</Grid>
                    <Grid size={4}>{fixedValue + " " + (measurement.unit ?? "")}</Grid>
                  </Grid>
                );
              })}
            </Box>
            <Box sx={{ my: 1, fontSize: "1rem", borderBottom: `1px solid ${surface.subtleDivider}`, pb: 1 }}>
              {sample.diagnostics.map((diagnostic, index) => {
                let fixedValue = diagnostic.unit !== undefined ? parseFloat(diagnostic.value).toFixed(2) : "-";
                return (
                  <Grid container key={diagnostic.displayName + index}>
                    <Grid size={8}>{diagnostic.displayName}</Grid>
                    <Grid size={4}>{fixedValue + " " + (diagnostic.unit ?? "")}</Grid>
                  </Grid>
                );
              })}
            </Box>
            {sample.analyzers.length > 0 &&
              <Box sx={{ fontSize: "1.1rem" }}>
                <Box sx={{ maxHeight: 600, overflow: "auto" }}>
                  <SampleAnalyzerDataCards analyzers={sample.analyzers} />
                </Box>
              </Box>
            }
          </Grid>
          <Grid size={1} sx={{ m:1 }}>
            <LinearProgress
              variant="indeterminate"
              color={getProgressColor()}
              sx={{
                height: '100%',
                borderRadius: 1,
                '& .MuiLinearProgress-bar': { animationDuration: '10s' },
                '& .MuiLinearProgress-bar1Indeterminate, & .MuiLinearProgress-bar2Indeterminate': {
                  left: 0,
                  right: 0,
                },
                '& .MuiLinearProgress-bar1Indeterminate': { animationName: `${verticalIndeterminate1}` },
                '& .MuiLinearProgress-bar2Indeterminate': { animationName: `${verticalIndeterminate2}` },
                margingLeft: 1,
              }}
            />
          </Grid>
        </Grid>
        <Box sx={{ borderTop: `1px solid ${surface.subtleDivider}`, pt: 1 }}>
          <Box sx={{ maxHeight: 65, overflow: "auto" }}>
            <Box sx={{ overflowX: "hidden" }}>
              {sample.sampleBgColor !== "green" &&
                sampleAlarms.length > 0 && sampleAlarms.map((alarm, index) => (
                  <Grid container key={index}>
                    <Grid size={2} sx={{ display: "flex", color: "warning.main" }}>
                      {alarm.category === "Device" && <i className="icon-sampleview-device" />}
                      {alarm.category === "SampleCondition" && <i className="icon-sampleview-sample" />}
                      {alarm.category === "Warning" && <i className="icon-sampleview-alarm" />}
                    </Grid>
                    <Grid size={10} sx={{ fontSize: "15px" }}>{alarm.brief}</Grid>
                  </Grid>
                ))
              }
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default SampleItem;
